import React, { useRef } from 'react';
import { Box, Typography } from '@heycater/design-system';
import { ButtonBase, Hidden, Menu } from '@material-ui/core';
import { AccountCircle } from '@material-ui/icons';
import PhoneIcon from '@material-ui/icons/Phone';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useBreakpointUp } from 'qualification/hooks/useBreakpoints';
import useCurrentUser from 'qualification/hooks/useCurrentUser';
import { CONTACT_PHONE } from 'shared/constants';
import { CUSTOMER_APP_HOST } from 'shared/helpers/urls';
import useAuthentication from 'shared/hooks/useAuthentication';
import {
  formatRating,
  formatRatingCount,
  getRating,
  getRatingCount,
} from 'shared/services/trustedShops/handleRatings';
import { Ratings } from 'shared/services/trustedShops/model';
import Logo from 'shared/svg/Logo';
import TrustedShopsLogoBlack from 'shared/svg/TrustedShopsLogoBlack';
import TrustedShopsStars from 'shared/svg/TrustedShopsStars';
import Container from 'system/components/Container';

interface Props {
  logoHref?: string;
  showLogin?: boolean;
  bgcolor?: string;
  ratings: Ratings;
}

function NavbarBase({
  logoHref,
  ratings,
  showLogin,
  bgcolor = 'inherit',
}: Props) {
  const isMediumScreen = useBreakpointUp('md');
  const mobile = !isMediumScreen;

  const { t } = useTranslation('common');

  return (
    <Navbar bgcolor={bgcolor}>
      <Container size="md">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <NavbarLogo href={logoHref} />
          {isMediumScreen && (
            <Box>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexWrap="nowrap"
              >
                <TrustedShopsBox>
                  <Stars>
                    <TrustedShopsStars />
                  </Stars>
                  <Text>
                    <strong>{formatRating(getRating(ratings))}</strong>
                    <RatingText>
                      &nbsp;{t('trustedShops.ratingText')}
                    </RatingText>
                  </Text>
                  <RatingCount>
                    {t('trustedShops.ratingCount', {
                      amount: formatRatingCount(getRatingCount(ratings)),
                    })}
                  </RatingCount>
                  <TSLogoBox>
                    <TrustedShopsLogoBlack />
                  </TSLogoBox>
                </TrustedShopsBox>
              </Box>
            </Box>
          )}
          <Box display="flex" alignItems="center">
            {showLogin && <UserMenu shortAccountText={mobile} />}

            <PhoneLink href={CONTACT_PHONE.href}>
              <Hidden smDown>
                <Box as="span" mr={0.5} lineHeight={1}>
                  <PhoneIcon fontSize="small" />
                </Box>
              </Hidden>
              {CONTACT_PHONE.formatted}
            </PhoneLink>
          </Box>
        </Box>
      </Container>
    </Navbar>
  );
}

const NavbarLogo = ({ href }: { href?: string }) => {
  if (href) {
    return (
      <Link href={href}>
        <NavbarLogoLink id="navbar-logo">
          <Logo />
        </NavbarLogoLink>
      </Link>
    );
  } else {
    return (
      <NavbarLogoLink as="div" id="navbar-logo">
        <Logo />
      </NavbarLogoLink>
    );
  }
};

export const UserMenu = ({
  shortAccountText,
}: {
  shortAccountText?: boolean;
}) => {
  const { currentUserAccount, loading } = useCurrentUser();
  const { logout } = useAuthentication();
  const isLoggedIn = !loading && currentUserAccount;
  const { t } = useTranslation('static_pages');

  const userMenuLinks = [
    { href: '/start', label: t('common:labels.requestCatering') },
    {
      href: `${CUSTOMER_APP_HOST}/account/requests`,
      label: t('common:navigation.events'),
    },
    {
      href: `${CUSTOMER_APP_HOST}/account`,
      label: t('common:navigation.account'),
    },
  ];

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (_event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(buttonRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const buttonRef = useRef<null | HTMLButtonElement>(null);

  if (loading) {
    return null;
  }

  return (
    <>
      <DropdownItem>
        {isLoggedIn ? (
          <>
            <ButtonBase
              ref={buttonRef}
              aria-controls="user-menu"
              aria-haspopup="true"
              onClick={handleClick}
              onMouseEnter={handleClick}
            >
              <Typography fontSize={14} fontWeight={500}>
                {shortAccountText
                  ? currentUserAccount?.firstName
                  : `${currentUserAccount?.firstName} ${currentUserAccount?.lastName}`}
              </Typography>

              <Box display="flex" alignItems="center" ml={1}>
                <AccountCircle />
              </Box>
            </ButtonBase>
            <StyledMenu
              id="user-menu"
              anchorEl={anchorEl}
              keepMounted
              transitionDuration={0}
              anchorOrigin={{
                vertical: 46,
                horizontal: 'left',
              }}
              PaperProps={{ elevation: 0 }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <Box display="contents" onMouseLeave={() => setAnchorEl(null)}>
                {userMenuLinks.map(({ href, label }) => (
                  <Link key={href} href={href} passHref>
                    <MenuItem>{label}</MenuItem>
                  </Link>
                ))}
                <MenuItem>
                  <ButtonBase onClick={logout}>
                    <Typography fontWeight={500}>
                      {t('common:navigation.signOut')}
                    </Typography>
                  </ButtonBase>
                </MenuItem>
              </Box>
            </StyledMenu>
          </>
        ) : (
          <ButtonBase
            id="open-login"
            href={`${CUSTOMER_APP_HOST}/account/login`}
          >
            <Typography fontSize={14} fontWeight={500}>
              {t('common:navigation.signIn')}
            </Typography>
          </ButtonBase>
        )}
      </DropdownItem>
    </>
  );
};

const StyledMenu = styled(Menu)`
  display: flex;
  flex-direction: column;

  .MuiPaper-root {
    border-radius: 0px;
    border: 1px solid ${(props) => props.theme.palette.grey[200]};
  }
`;

const DropdownContent = styled.div<{ visible?: boolean }>`
  background: white;
  min-height: 30px;
  overflow: hidden;
  text-align: left;
  position: absolute;
  top: 30px;
  z-index: ${(props) => props.theme.zIndex.appBar};
  padding: ${(props) => props.theme.spacing(1)}px;
  left: -80px;
  min-width: 200px;
  border-left: 1px solid ${(props) => props.theme.palette.grey[200]};
  border-right: 1px solid ${(props) => props.theme.palette.grey[200]};
  border-bottom: 1px solid ${(props) => props.theme.palette.grey[200]};
  display: none;
`;

const DropdownItem = styled.div`
  position: relative;
  &:hover ${DropdownContent} {
    display: block;
  }
`;

const MenuItem = styled.li`
  font-size: 16px;
  padding: ${({ theme }) => theme.spacing(0.75, 2)};
  cursor: pointer;

  &:hover {
    color: rgba(0, 0, 0, 0.6);
  }
`;

const Navbar = styled.nav<{ bgcolor: string }>`
  background-color: ${({ bgcolor }) => bgcolor};
  height: ${(props) => props.theme.navbar.height};
`;

const NavbarLogoLink = styled.a`
  width: 150px;
  height: ${(props) => props.theme.navbar.height};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  display: flex;
  align-items: center;

  & > span {
    display: inline-flex;
    margin-left: ${(props) => props.theme.spacing(1)}px;
    font-size: 18px;
  }

  svg {
    pointer-events: none;
  }
`;

const PhoneLink = styled.a`
  color: inherit;
  text-decoration: inherit;
  display: flex;
  align-items: center;
  padding-left: ${(props) => props.theme.spacing(3)}px;

  ${(props) => props.theme.breakpoints.down('xs')} {
    font-size: 12px;
  }
`;

const TrustedShopsBox = styled(Box)`
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
`;

const Stars = styled(Box)`
  width: 100%;
  min-width: 70px;
`;

const RatingText = styled.span`
  flex-shrink: 0;
`;

const RatingCount = styled.span`
  color: #737373;
  flex-shrink: 0;
`;

const TSLogoBox = styled(Box)`
  display: flex;
  align-items: center;
  min-height: ${(props) => props.theme.spacing(1.5)}px;
  padding-bottom: ${(props) => props.theme.spacing(0.5)}px;
`;

const Text = styled.span`
  flex-shrink: 0;
`;

export default NavbarBase;
